import { createLegacyThumbnail, ProductCardViewData } from '@services/MarppleShop/features/ProductList/type';
import {
  ProductEntity,
  ProductEntityProps,
  TLegacyStock,
} from '@services/MarppleShop/features/ProductList/application/types/product.entity.ts';
import {
  thumbnailTransformer,
  transformers,
} from '@services/MarppleShop/features/ProductList/application/transformers';
import { GoodsTypeId } from '@services/MarppleShop/features/Product/type';
import { Category, Metadata, Price, ProductRow, Review, SalePeriod, Status, StockVO, Store } from './types';
import { isNumber } from '@fxts/core';
import { isDtgProduct, isMpsProduct } from '@shared/product/goods-type-id.ts';
import { isPartnerCrew } from '@services/MarppleShop/shared/constant';

export class ProductMapper {
  /**
   * DB row -> ProductEntity
   */
  static fromDbRow(row: ProductRow): ProductEntity {
    const goodsTypeId = transformers.goodsTypeId(row.goods_type_id);

    const thumbnail = transformers.thumbnail(row);

    const store: Store = {
      id: row.store_id,
      crew_id: row.crew_id,
      displayName: row.store_name,
      domainName: row.domain_name,
      thumbUrl: row.profile_image_url,
      isAdultStore: row.adult_check_needed,
    };

    const review: Review = {
      avgScore: transformers.reviewScore(row.review_score),
      totalCount: transformers.reviewCount(row.review_count),
    };

    const salePeriod: SalePeriod = {
      startDate: row.sell_start_at,
      endDate: row.sell_end_at,
    };

    const category: Category = {
      rootId: row.cate_root_id,
      listId: row.cate_list_id,
      itemId: row.cate_item_id,
    };

    const localizedNames = transformers.getLocalizedNames(row);

    const status: Status = {
      isNft: row.is_token_gate ?? false,
      isLiked: row.is_liked,
      isMembership: row.is_membership,
      isPublic: row.is_public,

      // db에서 조회할 수 없는 값이라 false로 초기화
      isPopular: false,
      isFunding: false,
      isTrending: false,
      isBrandDeal: false,

      realTimeCurrentRank: null,
      prevTotalSalesAmount: null,
      currentTotalSalesAmount: null,
    };

    const metadata: Metadata = {
      ranking: row.ranking,
      watcherCount: row.watcher_count,
    };

    const price: Price = {
      krw: row.price,
      usd: row.price_en,
      jpy: row.price_jp,
    };

    // 재고 설정
    let stock;
    if (isLegacyStockProduct(goodsTypeId)) {
      const IS_QUANTITY_LIMITED = isNumber(row.quantity) && row.quantity > 0;
      if (IS_QUANTITY_LIMITED && isNumber(row.quantity)) {
        // Case 1) MPS, DTG 상품 & 한정 수량이 설정됨
        stock = new StockVO({
          availableStock: row.quantity - (row.order_count ?? 0),
          isStockPublic: !!row.is_quantity_public,
        });
      } else {
        // Case 2) MPS, DTG 상품 & 한정 수량이 설정되지 않음 -> 재고 무제한으로 설정
        stock = new StockVO({
          availableStock: Infinity,
          isStockPublic: !!row.is_quantity_public,
        });
      }
    } else {
      // Case 3) PBG, TPL 상품
      stock = new StockVO({
        availableStock: row.available_stock ?? 0,
        isStockPublic: !!row.is_quantity_public,
      });
    }

    const legacyStock: TLegacyStock = {
      quantity: isNumber(row.quantity) && row.quantity > 0 ? row.quantity : Infinity,
      order_count: row.order_count ?? 0,
      isStockPublic: !!row.is_quantity_public,
    };

    const props: ProductEntityProps = {
      id: row.id,
      stores_product_id: row.stores_product_id,
      store,
      thumbnail,
      review,
      price,
      salePeriod,
      category,
      localizedNames,
      status,
      metadata,

      // 재고
      stock,
      legacyStock,

      goodsTypeId,
      out_of_stock: row.out_of_stock,
      place_idx: row.place_idx ?? undefined,
      thumbnails: row.thumbnails,
      public_at: row.public_at,
      product_shipping_policy: {
        free_shipping_min_price: row.free_shipping_min_price,
        shipping_price: row.shipping_price,
      },
    };

    return new ProductEntity(props);
  }

  /**
   * ProductEntity -> ProductCardViewData
   */
  static toCardData(
    entity: ProductEntity,
    options: {
      isUserAdultVerified: boolean;
      locale: 'kr' | 'en' | 'jp';
    },
  ): Omit<ProductCardViewData, 'user_id'> {
    const adultThumbnail = thumbnailTransformer.createAdultThumbnail(options.locale);
    const thumbnail =
      !!entity.meta?.getAdultMeta() && !options.isUserAdultVerified ? adultThumbnail : entity.thumbnail;

    const legacyThumbnail = createLegacyThumbnail(thumbnail);

    const category = {
      root_id: entity.category.rootId,
      list_id: entity.category.listId,
      item_id: entity.category.itemId,
    };

    return {
      id: entity.id,
      stores_product_id: entity.storesProductId,

      is_partner: isPartnerCrew(entity.store.crew_id),

      store_id: entity.store.id,
      store_name: entity.store.displayName,
      domain_name: entity.store.domainName,
      profile_image_url: entity.store.thumbUrl,

      category,

      // 리뷰
      review_score: entity.review.avgScore,
      review_count: entity.review.totalCount,

      // 썸네일
      thumbnail_info: thumbnail,
      ...legacyThumbnail,

      // 재고
      quantity: entity.legacyStock.quantity,
      order_count: entity.legacyStock.order_count,
      out_of_stock: entity.outOfStock,
      is_quantity_public: entity.stock.isStockPublic,

      // 판매 기간
      sell_start_at: entity.salePeriod.startDate,
      sell_end_at: entity.salePeriod.endDate,

      // 가격
      price: entity.price.krw,
      price_en: entity.price.usd,
      price_jp: entity.price.jpy,

      name: entity.getDisplayName(options.locale),
      goods_type_id: entity.goodsTypeId,
      place_idx: entity.placeIdx,
      public_at: entity.publicAt,

      /**
       * 유저의 성인인증 유무
       */
      is_user_adult_verified: options.isUserAdultVerified,

      /**
       * 실시간 상품에서 마지막 주문일시 (toCardData 쪽에서 걷어내는게 좋을 듯)
       */
      ordered_at: entity.orderedAt,

      /**
       * status
       */
      is_liked: !!entity.status.isLiked,
      is_membership: entity.status.isMembership,
      is_token_gate: entity.status.isNft,
      is_public: entity.status.isPublic,
      is_funding: entity.status.isFunding,
      is_popular: entity.status.isPopular,
      is_trending: entity.status.isTrending,
      prev_total_sales_amount: entity.status.prevTotalSalesAmount,
      current_total_sales_amount: entity.status.currentTotalSalesAmount,
      realtime_current_rank: entity.status.realTimeCurrentRank,

      /**
       * metadata
       */
      ranking: entity.metadata.ranking,
      watcher_count: entity.metadata.watcherCount,

      stock: entity.stock.toJSON(),
      legacyStock: entity.legacyStock,
      adultMeta: entity.meta?.getAdultMeta()?.toJSON(),
      limitedMeta: entity.meta?.getLimitedMeta()?.toJSON(),
      lowStockMeta: entity.meta?.getLowStockThreshold()?.toJSON(),

      product_shipping_policy: entity.productShippingPolicy,
    };
  }
}

/**
 * 2025.09.11 개편으로 새로운 재고 로직이 pbg, tpl 상품에만 적용됨
 *
 * - mps, dtg 상품은 legacyStock 사용
 * - pbg, tpl 상품은 legacyStock 사용하지 않음
 *
 */
// 데이터
export const isLegacyStockProduct = (goodsTypeId: GoodsTypeId | unknown): goodsTypeId is 'MPS' | 'DTG' => {
  return isMpsProduct(goodsTypeId) || isDtgProduct(goodsTypeId);
};

// 객체
export function isLegacyStockProductData(data: {
  goodsTypeId: unknown;
}): data is { goodsTypeId: 'MPS' | 'DTG' };
export function isLegacyStockProductData(data: {
  goods_type_id: unknown;
}): data is { goods_type_id: 'MPS' | 'DTG' };
export function isLegacyStockProductData(data: { goodsTypeId?: unknown; goods_type_id?: unknown }): boolean {
  const typeId = 'goodsTypeId' in data ? data.goodsTypeId : data.goods_type_id;
  return isMpsProduct(typeId) || isDtgProduct(typeId);
}
